import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResultBadge, inr } from "./ResultBadge";

const Field = ({ label, children }) => (
  <label className="block text-xs text-slate-500 space-y-1"><span>{label}</span>{children}</label>
);

export function PlanPanel({ campaign, planCost, canEdit, onChange }) {
  const plan = campaign.plan || {}, creative = campaign.creative || {};
  const [f, setF] = useState({
    plan_status: plan.status || "draft", creative_status: creative.status || "pending",
    required_hashtag: plan.required_hashtag || "", brand_handle: plan.brand_handle || "",
    brand: campaign.brand || "", agency: campaign.agency || "",
    start_date: campaign.start_date || "", end_date: campaign.end_date || "", change_note: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/campaigns/${campaign.id}/plan`, f);
      toast.success(`Plan saved as v${(plan.version || 0) + 1} — re-run pre-flight to validate`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3" data-testid="plan-panel">
      <div className="lg:col-span-2 space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div><div className="font-heading font-bold text-slate-900">Plan v{plan.version || 0}</div><div className="text-xs text-slate-500">{plan.approved_at ? `Approved ${new Date(plan.approved_at).toLocaleDateString()} by ${plan.approved_by}` : "Not approved"}</div></div>
            <ResultBadge value={plan.status === "approved" ? "PASS" : "NEEDS_REVIEW"} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Brand"><Input value={f.brand} onChange={(e) => set("brand")(e.target.value)} disabled={!canEdit} data-testid="plan-brand" /></Field>
            <Field label="Agency / source"><Input value={f.agency} onChange={(e) => set("agency")(e.target.value)} disabled={!canEdit} data-testid="plan-agency" /></Field>
            <Field label="Campaign start"><Input type="date" value={f.start_date} onChange={(e) => set("start_date")(e.target.value)} disabled={!canEdit} data-testid="plan-start" /></Field>
            <Field label="Campaign end"><Input type="date" value={f.end_date} onChange={(e) => set("end_date")(e.target.value)} disabled={!canEdit} data-testid="plan-end" /></Field>
            <Field label="Unique campaign hashtag"><Input value={f.required_hashtag} onChange={(e) => set("required_hashtag")(e.target.value)} disabled={!canEdit} placeholder="#brand2026" data-testid="plan-hashtag" /></Field>
            <Field label="Brand handle to tag"><Input value={f.brand_handle} onChange={(e) => set("brand_handle")(e.target.value)} disabled={!canEdit} placeholder="@brandindia" data-testid="plan-brand-handle" /></Field>
            <Field label="Plan status">
              <Select value={f.plan_status} onValueChange={set("plan_status")} disabled={!canEdit}><SelectTrigger data-testid="plan-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="approved">Approved</SelectItem></SelectContent></Select>
            </Field>
            <Field label="Creative status">
              <Select value={f.creative_status} onValueChange={set("creative_status")} disabled={!canEdit}><SelectTrigger data-testid="creative-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pending">Pending brand approval</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select>
            </Field>
          </div>
          {canEdit && (
            <div className="flex items-end gap-3">
              <Field label="Change note"><Input value={f.change_note} onChange={(e) => set("change_note")(e.target.value)} placeholder="Why is this changing?" className="w-72" data-testid="plan-change-note" /></Field>
              <Button onClick={save} disabled={busy} className="bg-[#0052FF] hover:bg-[#0043d1]" data-testid="plan-save-btn">Save as v{(plan.version || 0) + 1}</Button>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between"><span className="font-semibold text-slate-800 text-sm">Planned deliverables ({(campaign.plan_deliverables || []).length})</span><span className="font-mono text-sm text-slate-700">{inr(planCost)} approved</span></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2">Creator</th><th className="px-4 py-2">Platform</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Expected</th><th className="px-4 py-2 text-right">Approved cost</th></tr></thead>
            <tbody>
              {(campaign.plan_deliverables || []).map((d) => (
                <tr key={d.id} className="border-t border-slate-100" data-testid="plan-deliverable-row">
                  <td className="px-4 py-2">{d.influencer_name} <span className="text-slate-400 font-mono text-xs">{d.handle}</span></td>
                  <td className="px-4 py-2">{d.platform}</td><td className="px-4 py-2">{d.type} #{d.seq}</td>
                  <td className="px-4 py-2 font-mono text-xs">{d.expected_date}</td>
                  <td className="px-4 py-2 text-right font-mono">{inr(d.approved_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-2"><span className="font-heading font-bold text-slate-900">Approved creative</span><ResultBadge value={creative.status === "approved" ? "PASS" : creative.status === "pending" ? "NEEDS_REVIEW" : "FAIL"} /></div>
          <div className="aspect-video rounded-lg bg-gradient-to-br from-slate-800 to-slate-950 flex items-center justify-center text-slate-400 text-xs font-mono mb-3">{creative.asset_name || "no creative reference"}</div>
          <div className="text-xs text-slate-500 space-y-1">
            <div>Version <span className="text-slate-800 font-mono">v{creative.version || 0}</span></div>
            <div>Approver <span className="text-slate-800">{creative.approver || "—"}</span></div>
            <div>Approved <span className="text-slate-800">{creative.approved_at ? new Date(creative.approved_at).toLocaleDateString() : "—"}</span></div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="plan-history">
          <div className="font-heading font-bold text-slate-900 mb-3">Version history</div>
          <ol className="relative border-l border-slate-200 ml-2 space-y-4">
            {[...(campaign.plan_history || [])].reverse().map((h) => (
              <li key={h.version} className="ml-4">
                <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-[#0052FF]" />
                <div className="text-xs font-semibold text-slate-800">v{h.version} · {h.changed_by} <span className="text-slate-400 font-normal">({h.role})</span></div>
                <div className="text-[11px] text-slate-500">{new Date(h.changed_at).toLocaleString()}</div>
                <div className="text-xs text-slate-600 mt-0.5">{h.note || Object.keys(h.changes || {}).join(", ")}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
